// File: AddBlogForm.jsx
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultEditor from "react-simple-wysiwyg";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  slug: Yup.string().required("Slug is required"),
  metaTitle: Yup.string().required("Meta title is required"),
  metaDescription: Yup.string().required("Meta description is required"),
  title: Yup.string().required("Title is required"),
  shortDescription: Yup.string().required("Short description is required"),
  keywords: Yup.string().required("Keywords are required"),
  tags: Yup.string().required("Tags are required"),
  categories: Yup.array().min(1, "At least one category must be selected"),
  description: Yup.string().required("Detailed description is required"),
  blogThumbnail: Yup.string().required("Blog thumbnail is required"),
  blogImage: Yup.string().required("Blog image is required"),
});

const styles = {
  formContainer: {
    minHeight: "100vh",
    padding: "20px 20px 0px 20px",
    background: "#f5f7fa",
  },
  card: {
    // backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "0px 15px 15px 15px",
  },
  label: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#484848",
    display: "block",
    marginBottom: "0.25rem",
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
  },
  error: {
    color: "#e74c3c",
    fontSize: "12px",
    marginTop: "4px",
  },
  filePreview: {
    width: "100px",
    height: "100px",
    objectFit: "cover",
    borderRadius: "6px",
    marginRight: "10px",
    marginTop: "10px",
    position: "relative",
  },
  removeIcon: {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    backgroundColor: "#e74c3c",
    color: "#fff",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
  },
  backBtn: {
    padding: "6px 12px",
    background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "1px solid #ccc",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitBtn: {
    padding: "10px 20px",
       background: "linear-gradient(to bottom right, #163252, #0081ff)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
    width:"100%"
  },
};

const getAllCategories = async () => {
  const res = await apiRequestHandler({
    endPoint: "getAdminCategory",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res?.data?.map((cat) => ({ value: cat._id, label: cat.name })) || [];
};

const AddBlogForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const blog = state?.blog;
  const mode = state?.mode || "add";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const [loading, setLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    getAllCategories().then(setCategoryOptions);
  }, []);

  const handleImageUpload = async (e, setFieldValue, field) => {
    const file = e.target.files[0];
    if (!file?.type.startsWith("image/")) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await apiRequestHandler({
      method: "POST",
      endPoint: "uploadFile",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res?.data?.url) setFieldValue(field, res.data.url);
    else toast.error(`Failed to upload ${field}`);
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    const res = await apiRequestHandler({
      method: "POST",
      endPoint: "addBlog",
      data: { ...values, blogId: blog?._id },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    setLoading(false);

    if (res?.success) {
      toast.success(res.message);
      navigate("/admin/blog-list");
    } else {
      toast.error(res?.message || "Failed to submit blog");
    }

    resetForm();
  };

  return (
    <div style={styles.formContainer}>
      <div style={styles.card}>


        <Formik
          enableReinitialize
          initialValues={{
            slug: blog?.slug || "",
            metaTitle: blog?.metaTitle || "",
            metaDescription: blog?.metaDescription || "",
            title: blog?.title || "",
            shortDescription: blog?.shortDescription || "",
            keywords: blog?.keywords || "",
            tags: blog?.tags || "",
            categories: blog?.category?.map((c) => ({
              value: c._id,
              label: c.name,
            })) || [],
            description: blog?.description || "",
            blogThumbnail: blog?.blogThumbnail || "",
            blogImage: blog?.blogImage || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form className="row g-4">
              {/* Section 1: Meta Data */}
              <div className=" col-12 bg-white custom-rounded">
                <div className="d-flex justify-content-between flex-row-reverse align-items-center   p-3  ">
                  <div onClick={() => navigate(-1)} style={styles.backBtn}>← Back</div>
                  <h4 style={{ marginBottom: "0px" }}>
                    {isViewMode ? "View Blog" : isEditMode ? "Edit Blog" : "Add New Blog"}
                  </h4>
                </div>
              </div>
               <h5 className="custom-head">Meta Data</h5>

              <div className="col-12  bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={styles.label}>Slug</label>
                    <Field name="slug" style={styles.input} placeholder={"Enter slug"} readOnly={isViewMode} />
                    <ErrorMessage name="slug" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Meta Title</label>
                    <Field name="metaTitle" style={styles.input}placeholder={"Enter title"} readOnly={isViewMode} />
                    <ErrorMessage name="metaTitle" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Meta Description</label>
                    <Field name="metaDescription" style={styles.input} placeholder={"Enter description"} readOnly={isViewMode} />
                    <ErrorMessage name="metaDescription" component="div" style={styles.error} />
                  </div>
                   <div className="col-md-6">
                    <label style={styles.label}>Keywords</label>
                    <Field name="keywords" style={styles.input}placeholder={"Enter keywords"} readOnly={isViewMode} />
                    <ErrorMessage name="keywords" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Tags</label>
                    <Field name="tags" style={styles.input} placeholder={"Enter enter tags"} readOnly={isViewMode} />
                    <ErrorMessage name="tags" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 2: Blog Details */}
               <h5 className="custom-head">Blog Details</h5>

              <div  className="col-12  bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={styles.label}>Title</label>
                    <Field name="title" style={styles.input} placeholder={"Enter title"} readOnly={isViewMode} />
                    <ErrorMessage name="title" component="div" style={styles.error} />
                  </div>
                  <div className="col-md-6">
                    <label style={styles.label}>Short Description</label>
                    <Field as="textarea" name="shortDescription" placeholder={"Enter description"} style={styles.input} readOnly={isViewMode} />
                    <ErrorMessage name="shortDescription" component="div" style={styles.error} />
                  </div>
                </div>
              </div>

              {/* Section 3: Images */}
               <h5 className="custom-head">Images</h5>

              <div  className="col-12  bg-white p-3 custom-rounded" style={styles.card}>
                <div className="row g-3">
                  {["blogThumbnail", "blogImage"].map((field, i) => (
                    <div className="col-md-6" key={field}>
                      <label style={styles.label}>
                        {field === "blogThumbnail" ? "Blog Thumbnail" : "Blog Image"}
                      </label>
                      {!isViewMode && (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, setFieldValue, field)}
                          style={styles.input}
                        />
                      )}
                      {values[field] && (
                        <div className="position-relative">
                          <img src={values[field]} alt={field} style={styles.filePreview} />
                          {!isViewMode && (
                            <span
                              style={styles.removeIcon}
                              onClick={() => setFieldValue(field, "")}
                            >✖</span>
                          )}
                        </div>
                      )}
                      <ErrorMessage name={field} component="div" style={styles.error} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 5: Description */}
               <h5 className="custom-head">Detailed Description</h5>

              <div  className="col-12  bg-white p-3 custom-rounded" style={styles.card}>
                <DefaultEditor
                  value={values.description}
                  onChange={(e) => setFieldValue("description", e.target.value)}
                  disabled={isViewMode}
                />
                <ErrorMessage name="description" component="div" style={styles.error} />
              </div>

              {/* Section 6: Categories */}
               <h5 className="custom-head">Categories</h5>

              <div  className="col-12  bg-white p-3 custom-rounded" style={styles.card}>
                <Select
                  isMulti
                  name="categories"
                  options={categoryOptions}
                  value={values.categories}
                  isDisabled={isViewMode}
                  onChange={(val) => setFieldValue("categories", val)}
                />
                <ErrorMessage name="categories" component="div" style={styles.error} />
              </div>

              {/* Submit Button */}
              {!isViewMode && (
                <div className="col-12 text-center">
                  <button type="submit" style={styles.submitBtn} disabled={loading}>
                    {loading ? "Submitting..." : isEditMode ? "Update Blog" : "Create Blog"}
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddBlogForm;
